import React from 'react';
import { Target, Sparkles, Users, Zap, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { FinalCTA } from '../components/Sections';
import { VALUES, TRUST_PILLARS, COMMITMENTS } from '../mock';
import { Button } from '../components/ui/button';
import { ShieldCheck, Lock, Rocket, Headphones, MessagesSquare } from 'lucide-react';

const valueIcons = { Target, Sparkles, Users, Zap };
const pillarIcons = { ShieldCheck, Lock, Rocket, HeadphonesIcon: Headphones, MessagesSquare };

const Sobre = () => {
  const navigate = useNavigate();
  return (
    <>
      <Seo
        title="Sobre nós"
        description="Conheça a CoreDesk: unimos inteligência artificial de verdade e experiência humana para reinventar o atendimento das empresas no Brasil."
        path="/sobre"
      />
      <Header />
      <PageHero
        eyebrow="Sobre a CoreDesk"
        title="Estamos aqui para reinventar |o atendimento no Brasil"
        subtitle="Nascemos com uma missão clara: unir inteligência artificial de verdade e experiência humana em uma plataforma que empresas de qualquer tamanho possam usar do dia um."
      />

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p className="text-xl text-[#0a0e27] font-semibold leading-relaxed">
              Toda vez que um cliente não recebe resposta, uma empresa perde uma oportunidade.
            </p>
            <p className="mt-6 text-slate-600 leading-relaxed text-lg">
              A CoreDesk nasceu para resolver isso. Depois de anos vendo negócios brasileiros lutando com ferramentas caras, complicadas e desatualizadas, decidimos construir a plataforma que gostaríamos de usar: <b>simples de começar, poderosa de escalar</b>.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">
              Combinamos <b>IA generativa</b>, <b>WhatsApp API Oficial</b> e uma <b>UX pensada para o dia a dia</b> de quem atende. E o mais importante: com time de suporte, produto e engenharia inteiramente no Brasil, falando a sua língua e entendendo a sua realidade.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#FAF6F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">Nossos valores</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a0e27]">O que nos move todos os dias</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => {
              const Icon = valueIcons[v.icon];
              return (
                <div key={v.title} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition">
                  <div className="w-11 h-11 rounded-xl bg-[#FFF1EA] flex items-center justify-center">
                    <Icon size={20} className="text-[#FF5B24]" />
                  </div>
                  <h3 className="mt-4 font-extrabold text-lg text-[#0a0e27]">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-20 bg-[#0a0e27] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FF5B24]/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">Nossos compromissos</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-white">O que você pode esperar da CoreDesk</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {COMMITMENTS.map((c) => (
              <div key={c.k} className="text-center">
                <div className="text-5xl font-black text-[#FF5B24]">{c.k}</div>
                <div className="mt-2 text-sm text-white/70">{c.v}</div>
              </div>
            ))}
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUST_PILLARS.map((p) => {
              const Icon = pillarIcons[p.icon] || ShieldCheck;
              return (
                <div key={p.title} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <Icon size={20} className="text-[#FF5B24]" />
                  <div className="mt-3 font-bold text-white">{p.title}</div>
                  <div className="mt-1 text-xs text-white/60 leading-relaxed">{p.desc}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-14 text-center">
            <Button onClick={() => navigate('/contato')} className="btn-coral rounded-full h-12 px-6 text-sm font-semibold gap-1.5">
              Fale com nosso time <ArrowUpRight size={16} />
            </Button>
          </div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </>
  );
};

export default Sobre;
